// User role types for authentication and filtering
export type UserRole = 'runner' | 'coach' | 'admin' | 'hybrid' | 'lord';

// Roles that get coach-level access (own roster, coach portal, etc). 'hybrid' is
// a runner who is also an active coach for the current season; 'lord' has no
// roster of its own but still needs to reach coach-portal-style screens (Small
// Council, Club NPS Scores) — see v_rhwb_roles in
// cloud-sql/add-lord-role-to-v_rhwb_roles.sql. Keep this in sync with
// COACH_ROLES in cloud-run/qual-scores-api/routes/middleware.js (can't share
// code across the TS/CommonJS boundary); role-constants.test.js asserts the two
// match.
export const COACH_ROLES: UserRole[] = ['coach', 'hybrid', 'admin', 'lord'];

// v_rhwb_roles returns lowercase today and the API lowercases what it reads, but
// these helpers are called with whatever a caller happens to hold (a cached
// session, a hand-built test fixture, a future source of role strings), so fold
// case here rather than trusting every call site. Backend twin: normalizeRole in
// cloud-run/qual-scores-api/routes/middleware.js.
const normalize = (role?: UserRole | string | null): string =>
  typeof role === 'string' ? role.toLowerCase() : '';

export const hasCoachView = (role?: UserRole | null): boolean =>
  (COACH_ROLES as string[]).includes(normalize(role));

export const hasRunnerView = (role?: UserRole | null): boolean =>
  normalize(role) === 'runner' || normalize(role) === 'hybrid';

/**
 * The persona a multi-entitlement user is currently acting as. Distinct from
 * UserRole: `hybrid` is an entitlement ("this person is both"), not something
 * the UI can render, so it has no persona of its own — a hybrid picks between
 * the `coach` and `runner` personas instead. `lord` likewise has no persona:
 * its oversight is expressed by rendering as `admin`, which is the persona that
 * already carries a coach picker.
 *
 * Which personas a given user may choose is resolved server-side (from
 * v_rhwb_roles plus whether they actually appear in rhwb_coaches /
 * runner_season_info) and returned by /api/whoami. Never derive it on the
 * client: most admins are not coaches, and some are not runners either.
 */
export type Persona = 'runner' | 'coach' | 'admin';

export const isAdminRole = (role?: UserRole | null): boolean =>
  normalize(role) === 'admin';

// 'lord' is cross-coach oversight split out of 'admin' - can target an arbitrary
// coach/runner the same way admin can (Know Your Runner, Coach Insights, NPS
// Scores, the ?email_id= profile override, etc), but does not get Admin
// Pipeline access — that stays gated by isAdminRole. Backend twin: isAdminOrLord
// in cloud-run/qual-scores-api/routes/middleware.js.
export const hasCoachOversight = (role?: UserRole | null): boolean =>
  normalize(role) === 'admin' || normalize(role) === 'lord';

/**
 * Shown when someone signs in to Pulse (web or mobile) with an email that isn't
 * an RHWB member's. Same wording as notMemberMessage in
 * cloud-run/qual-scores-api/lib/membership.js, which the API sends when it
 * refuses the sign-in email.
 */
export const notMemberMessage = (email: string): string =>
  `We couldn't find an RHWB membership for ${email}. Please sign in with the email address you registered with.`;
